import React, { FC, ReactElement, useRef, useState } from 'react';
import styled from 'styled-components';

import Group from '../group/Group';
import type { SetProps } from './Set';
import { SetDesigned } from './Set';
import ElementPlaceholder from './elementPlaceholder/ElementPlaceholder';

type SetEditorProps = {
  setSettings: any;
  options: any;
};

type EditorProps = SetProps & SetEditorProps;

const SetDesignedEditor = styled(SetDesigned)`
  &.set {
    &:hover {
      outline: 1px solid rgba(63, 162, 254, 1);
    }
    .emptyGroup {
      width: ${(props) => props.groupsRatio[0] + '%' || props.groupsRatio[1] + '%'};
    }
  }
`;

const SetEditor: FC<EditorProps> = ({ ...props }): ReactElement => {
  const useAppSelector = props.editorPlugins.useAppSelector;
  const useAppDispatch = props.editorPlugins.useAppDispatch;
  const selectActive = props.editorPlugins.selectActive;
  const setActiveType = props.editorPlugins.setActiveType;
  const setActiveProps = props.editorPlugins.setActiveProps;
  const updateData = props.editorPlugins.updateData;
  const trackStory = props.editorPlugins.trackStory;
  const openElementsLibrary = props.editorPlugins.openElementsLibrary;
  const selectActiveSet = props.editorPlugins.selectActiveSet;
  const selectActiveSettings = props.editorPlugins.selectActiveSettings;
  const setSettingsTitle = props.editorPlugins.setSettingsTitle;
  const Duplicate = props.editorPlugins.duplicateButton;
  const Delete = props.editorPlugins.deleteButton;
  const Add = props.editorPlugins.addButton;
  const Draggable = props.editorPlugins.draggable;
  const scrollTo = props.editorPlugins.scrollTo;
  const addUniqueKeysToSet = props.editorPlugins.addUniqueKeysToSet;

  const activePath: any = useAppSelector((state: any) => state.editor.present.activePath);
  const globalData: any | null = useAppSelector((state: any) => state.editor.present.data);
  let setIndex = 0;
  if (props.path) setIndex = props.path.setIndex;
  const dispatch = useAppDispatch();
  const settings = props.setSettings;
  let isEmptySet = checkIfSetEmpty();
  const [isOptions, setIsOptions] = useState(false);

  function checkIfSetEmpty() {
    let isEmpty = true;
    props.groups.map((groupData: any, index: number) => {
      if (groupData.length) return (isEmpty = false);
    });
    return isEmpty;
  }

  function handleClick(event: any) {
    if (
      event.target === event.currentTarget ||
      event.target.classList.contains('emptyGroup') ||
      event.target.classList.contains('addElementPlaceholder') ||
      event.target.classList.contains('setInnerWrapper')
    ) {
      event.preventDefault();
      dispatch(
        trackStory({ event: 'click on set', screen: 'editorv4', params: { editorVersion: 'V4' } }),
      );
      return selectTheActiveSet();
    }
  }

  function getPath(index: number) {
    if (!props.path) return null;
    let path = { ...props.path };
    path.groupIndex = index;
    return path;
  }

  function clickOpenElementsLibrary() {
    dispatch(
      trackStory({
        event: 'click on add element from placeholder component',
        screen: 'editorv4',
        params: {},
      }),
    );
    let elementPath = { ...props.path };
    elementPath.groupIndex = 0;
    elementPath.elementIndex = 0;
    let data = {
      path: elementPath,
      type: 'element',
    };
    dispatch(selectActiveSet(''));
    return dispatch(openElementsLibrary(data));
  }

  function isSetActive() {
    if (!activePath) return false;
    if (!props.path) return false;
    return JSON.stringify(activePath) === JSON.stringify(props.path);
  }

  function selectTheActiveSet() {
    dispatch(trackStory({ event: 'success active set', screen: 'editorv4', params: {} }));
    dispatch(setActiveType('set'));
    let cleanProps = { ...props };
    delete cleanProps.editorPlugins;
    dispatch(setActiveProps(cleanProps));
    settings ? dispatch(selectActiveSettings({ settings })) : dispatch(selectActiveSettings(null));
    dispatch(setSettingsTitle('Set'));
    return dispatch(selectActive(props.path));
  }

  function duplicateSet() {
    dispatch(
      trackStory({
        event: 'click duplicate set from main container',
        screen: 'editorv4',
        params: {},
      }),
    );
    let sets = [...globalData.slides[activePath.slideIndex].combos[activePath.comboIndex].sets];
    sets.splice(
      setIndex,
      0,
      addUniqueKeysToSet ? addUniqueKeysToSet(props, { forceAdd: true }) : setIndex + 1,
    );
    let data = {
      path: activePath,
      data: sets,
      object: 'set',
    };
    dispatch(updateData(data));
    dispatch(selectActive({ ...activePath, setIndex: activePath.setIndex + 1 }));
    setTimeout(
      () => scrollToAddedSet(activePath.slideIndex, activePath.comboIndex, activePath.setIndex + 1),
      500,
    );
  }

  function scrollToAddedSet(slideIndex: number, comboIndex: number, setIndex: number) {
    let slides = document.querySelectorAll('.mainStory .slide');
    let slide = slides[slideIndex] as HTMLElement;
    if (!slide) return;
    let combos = slide.querySelectorAll('.inner-wrapper .comboWrapper.comboDragWrapper');
    const combo = combos[comboIndex] as HTMLElement;
    if (!combo) return;
    let sets = combo.querySelectorAll('.set');
    const set = sets[setIndex] as HTMLElement;
    if (set) scrollTo(set, 'center');
  }

  function deleteSet() {
    dispatch(
      trackStory({ event: 'click delete set from main container', screen: 'editorv4', params: {} }),
    );
    let sets = [...globalData.slides[activePath.slideIndex].combos[activePath.comboIndex].sets];
    sets.splice(setIndex, 1);
    let data = {
      path: activePath,
      data: sets,
      object: 'set',
    };
    dispatch(updateData(data));
    let newPath = { ...activePath };
    if (sets.length === 1) {
      newPath.setIndex = 0;
    } else if (activePath.setIndex === sets.length) {
      newPath.setIndex = activePath.setIndex - 1;
    }
    dispatch(selectActive(newPath));
    setTimeout(
      () => scrollToAddedSet(newPath.slideIndex, newPath.comboIndex, newPath.setIndex),
      500,
    );
  }

  function isHideDelete() {
    if (
      !props.path ||
      globalData.slides[props.path.slideIndex] === null ||
      !globalData.slides[props.path.slideIndex].combos.length ||
      !globalData.slides[props.path.slideIndex].combos[props.path.comboIndex].sets.length
    )
      return false;
    return (
      props.path &&
      globalData.slides[props.path.slideIndex].combos[props.path.comboIndex].sets.length === 1
    );
  }

  function checkForMainMediaInMobile(groupData: any) {
    if (groupData.length !== 1) return 1;
    if (
      groupData[0].name === 'Image' ||
      groupData[0].name === 'Video' ||
      groupData[0].name === 'Charts'
    )
      return 0;
    return 1;
  }

  function optionAction(event: any, option: any) {
    event.stopPropagation();
    dispatch(
      trackStory({
        event: 'click ' + option.name + ' icon from active set options',
        screen: 'editorv4',
        params: {},
      }),
    );
    dispatch(selectActiveSet(option.settingTitle));
  }

  return (
    <Draggable
      draggableId={
        props.path?.slideIndex + '-' + props.path?.comboIndex + '-' + props.path?.setIndex
      }
      index={props.index}
      isDragDisabled={props.disableDND}
      key={props.uniqueKey}>
      {(provided: any, snapshot: any) => (
        <SetDesignedEditor
          ref={provided.innerRef}
          {...provided.draggableProps}
          {...provided.dragHandleProps}
          className={
            'set ' +
            (isSetActive() ? 'activeMode ' : '') +
            (isEmptySet ? 'empty ' : '') +
            (props.isTab ? 'tab ' : '') +
            (props.activeTab ? 'activeTab ' : '') +
            (props.isActiveNarrator ? 'activeNarrator' : '')
          }
          groupsRatio={props.groupsRatio}
          horizontalAlignment={props.horizontalAlignment}
          verticalAlignment={props.verticalAlignment}
          borderRadius={props.round}
          borderWidth={props.borderWidth}
          borderColor={props.borderColor}
          backgroundColor={props.backgroundColor}
          data-anchor-target={props.isAnchorActive ? props.anchorName : ''}
          onMouseDown={(event) => {
            handleClick(event);
          }}>
          <div className="setInnerWrapper">
            <div className="options">
              <div className="left">
                <div className="elementName">Group</div>
                {props.options && (
                  <div className={'optionsWrapper ' + (!isOptions ? 'noOptions' : '')}>
                    {props.options.length &&
                      props.options.map((option: any, index: number) => {
                        let passCondition = props.options.name === option.condition;
                        if (!option.condition || (option.condition && passCondition)) {
                          setIsOptions(true);
                          return (
                            <div
                              key={index}
                              className={'option ' + option.name}
                              onClick={(event) => optionAction(event, option)}>
                              <img src={option.icon} alt={option.title} />
                              {option.title}
                            </div>
                          );
                        }
                      })}
                  </div>
                )}
              </div>
              <div className="right">
                <div className={'permanentOptions'}>
                  <div
                    className={'option duplicate ' + (isHideDelete() ? 'noAfter' : '')}
                    onClick={() => {
                      duplicateSet();
                    }}>
                    <Duplicate isOption={true} />
                  </div>
                  <div
                    className={'option delete ' + (isHideDelete() ? 'hidden' : '')}
                    onClick={() => {
                      deleteSet();
                    }}>
                    <Delete isOption={true} />
                  </div>
                </div>
              </div>
            </div>
            {props.groups.map((groupData: any, index: number) => {
              if (groupData.length) {
                let order = 1;
                if (window.innerWidth < 768) order = checkForMainMediaInMobile(groupData);
                return (
                  <Group
                    key={index}
                    elements={groupData}
                    width={props.groupsRatio[index]}
                    editorPlugins={props.editorPlugins}
                    index={index}
                    path={getPath(index)}
                    mobileOrder={order}
                  />
                );
              } else {
                return (
                  <div key={index} className={'emptyGroup pos-' + index}>
                    <ElementPlaceholder
                      path={props.path}
                      groupIndex={index}
                      editorPlugins={props.editorPlugins}
                      isEmptySlide={props.isEmptySlide}
                    />
                  </div>
                );
              }
            })}
            {/* {
                            isEmptySet && <div className={'addElement '} onClick={() => {
                                clickOpenElementsLibrary();
                            }}><Add/></div>
                        } */}
          </div>
        </SetDesignedEditor>
      )}
    </Draggable>
  );
};

export default SetEditor;
